import React, { FunctionComponent } from "react";
import styles from "./TextVideoBanner.module.scss";
import TextVideoBannerBlockProps from "./TextVideoBannerProps.interface";

import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import Button from "components/Button/Button";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import VideoViewer from "components/VideoViewer/VideoViewer";
import Paragraph from "components/Paragraph/Paragraph";
import setInViewClasses from "blocks/hooks/setInViewClasses";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import BoxWrap from "components/BoxWrap/BoxWrap";
import VideoEmbed from "components/VideoEmbed/VideoEmbed";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useAnchor from "blocks/hooks/useAnchor";

const TextVideoBanner: FunctionComponent<TextVideoBannerBlockProps> = (props): JSX.Element => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);
    const { inViewElementRef, inViewClasses } = setInViewClasses(styles);

    const identifier = useAnchor(props.settings.anchorMenuLabel);
    const { splitClasses, splitStyles } = useSplitBackground(props);

    const bannerSizeClass = props.settings.bannerSize ? styles[`size_${props.settings.bannerSize}`] : styles.size_Medium;
    const headlineCaseClass = props.settings.headlineCase ? styles[`case_${props.settings.headlineCase}`] : undefined;

    const video = props.content?.video?.[0] && props.content?.video.length > 0 ? props.content.video[0].content : undefined;
    const videoPositionClass = props.settings.videoPosition === "Media Right" ? styles.has_video_right : undefined;
    const videoOverflowClass = props.settings.videoOverflow === true ? styles.has_overflow : styles.has_split;

    const isAutoplay = video && video.videoAutoplay ? video.videoAutoplay : true;
    const isLoop = video && video.videoLoop ? video.videoLoop : true;
    const showControls = !isAutoplay;

    return (
        <section className={combineClasses(styles.wrapper, ...wrapperClasses)} style={{ ...wrapperStyles, ...splitStyles }} id={identifier}>
            <div className={combineClasses(styles.container, ...splitClasses, videoOverflowClass, bannerSizeClass, videoPositionClass, inViewClasses)} ref={inViewElementRef}>
                <div className={styles.media}>
                    <div className={styles.media_inner}>
                        {/* Local Video */}
                        {video && video._contentType === "blockVideoNestedLocal" && video.video?.umbracoFile ? (
                            <VideoViewer className={styles.video} src={video.video?.umbracoFile} poster={video?.poster?.umbracoFile?.src} alt={props.content.videoAlternative} autoplay={isAutoplay} loop={isLoop} controls={showControls} aspectRatioX={1} aspectRatioY={1} />
                        ) : null}

                        {/* Video Embed */}
                        {video && video._contentType === "blockVideoNestedYouTube" && video.videoUrl ? (
                            <VideoEmbed className={styles.video} src={video.videoUrl} alt={props.content.videoAlternative} autoplay={isAutoplay} loop={isLoop} aspectRatioX={1} aspectRatioY={1} />
                        ) : null}
                    </div>
                </div>

                <div className={styles.content}>
                    <div className={styles.content_inner}>
                        {/* TODO: Refactor BoxWrap Component to useBoxWrap hook */}
                        <BoxWrap {...props?.settings?.boxWrap && props.settings.boxWrap?.[0]} className={combineClasses(styles.inner, inViewClasses)} ref={inViewElementRef}>
                            {/* Tagline */}
                            {props.content.tagline ? (
                                <span className={styles.tagline}>{props.content.tagline}</span>
                            ) : null}

                            {/* Headline */}
                            {props.content.headline ? (
                                <DynamicHeading className={combineClasses(styles.headline, headlineCaseClass)} text={props.content.headline} headlineSize={props.settings.headlineSize} headlineWeight={props.settings.headlineWeight} />
                            ) : null}

                            {/* Text */}
                            {props.content.bodyText ? (
                                <Paragraph className={styles.text} innerHTML={props.content.bodyText} />
                            ) : null}

                            {/* Buttons */}
                            {props.content.buttons && props.content.buttons.length > 0 ? (
                                <div className={styles.buttons}>
                                    {props.content.buttons.map((button, index) => {
                                        return (
                                            <Button key={index} to={button.content.link} className={styles.button} variant={button.content.variant} {...button.content}>{button.content.label}</Button>
                                        );
                                    })}
                                </div>
                            ) : null}
                        </BoxWrap>
                    </div>
                </div>
            </div>

            {props.settings.backgroundMedia?.[0] && props.settings.backgroundMedia.length > 0 ? (
                <BackgroundMedia key={props.contentUdi} {...props.settings.backgroundMedia[0]} />
            ) : null}
        </section>
    );
};

export default TextVideoBanner;